import { supabase } from '../lib/supabase';
import CryptoJS from 'crypto-js';

// Simple obfuscation - not visible in Supabase, deters casual viewing
// Split key to make it less obvious in code search
const _k = ['Ab', 'Cv', '1l', 'Pr', '0f', '1t', 'K3', 'y!'].join('');

const encryptValue = (value) => {
  return CryptoJS.AES.encrypt(value.toString(), _k).toString();
};

const decryptValue = (encrypted) => {
  try {
    const bytes = CryptoJS.AES.decrypt(encrypted, _k);
    const decrypted = bytes.toString(CryptoJS.enc.Utf8);
    return parseFloat(decrypted);
  } catch (e) {
    console.error('Failed to decrypt value:', e);
    return 0;
  }
};

// State name to abbreviation mapping
const STATE_ABBREVIATIONS = {
  'alabama': 'AL', 'alaska': 'AK', 'arizona': 'AZ', 'arkansas': 'AR',
  'california': 'CA', 'colorado': 'CO', 'connecticut': 'CT', 'delaware': 'DE',
  'deleware': 'DE', // Common misspelling
  'florida': 'FL', 'flordia': 'FL', // Common misspelling
  'georgia': 'GA', 'hawaii': 'HI', 'idaho': 'ID', 'illinois': 'IL',
  'indiana': 'IN', 'iowa': 'IA', 'kansas': 'KS', 'kentucky': 'KY',
  'louisiana': 'LA', 'maine': 'ME', 'maryland': 'MD', 'massachusetts': 'MA',
  'michigan': 'MI', 'minnesota': 'MN', 'mississippi': 'MS', 'missouri': 'MO',
  'montana': 'MT', 'nebraska': 'NE', 'nevada': 'NV', 'new hampshire': 'NH',
  'new jersey': 'NJ', 'new mexico': 'NM', 'new york': 'NY', 'north carolina': 'NC',
  'north dakota': 'ND', 'ohio': 'OH', 'oklahoma': 'OK', 'oregon': 'OR',
  'pennsylvania': 'PA', 'rhode island': 'RI', 'south carolina': 'SC',
  'south dakota': 'SD', 'tennessee': 'TN', 'texas': 'TX', 'utah': 'UT',
  'vermont': 'VT', 'virginia': 'VA', 'washington': 'WA', 'west virginia': 'WV',
  'wisconsin': 'WI', 'wyoming': 'WY'
};

// Normalize state to 2-letter abbreviation
const normalizeState = (state) => {
  if (!state) return null;
  const trimmed = state.trim().toUpperCase();
  // Already an abbreviation (2 letters)
  if (/^[A-Z]{2}$/.test(trimmed)) return trimmed;
  // Look up full name
  const abbr = STATE_ABBREVIATIONS[state.trim().toLowerCase()];
  return abbr || state.trim(); // Return original if not found
};

// Acreage range buckets for filtering
const ACREAGE_RANGES = [
  { label: '< 1 acre', min: 0, max: 1 },
  { label: '1-5 acres', min: 1, max: 5 },
  { label: '5-10 acres', min: 5, max: 10 },
  { label: '10-25 acres', min: 10, max: 25 },
  { label: '25+ acres', min: 25, max: Infinity }
];

export class ProfitAnalysisService {

  static ACREAGE_RANGES = ACREAGE_RANGES;
  static RATE_WINDOW_WEEKS = 26;
  static NON_PROJECT_ALLOWANCE_HOURS_PER_WEEK = 5;
  static STANDARD_HOURS_PER_WEEK = 40;
  static WORKERS_COMP_RATE = 0.003;
  static RECOVERABLE_PAYROLL_SHARE =
    (ProfitAnalysisService.STANDARD_HOURS_PER_WEEK -
      ProfitAnalysisService.NON_PROJECT_ALLOWANCE_HOURS_PER_WEEK) /
    ProfitAnalysisService.STANDARD_HOURS_PER_WEEK;

  /**
   * Fix contractor payroll records that were encrypted with wrong key
   * Run from browser console: window.fixContractorPayroll()
   * Updates existing records by ID with correctly encrypted gross_pay values
   */
  static async fixContractorPayroll() {
    // Map of record ID -> correct gross_pay amount (from Wise/PayPal CSVs)
    const recordFixes = [
      // Glarence - Wise payments
      { id: '9f078d8c-6981-43b6-866a-75844b0774ac', gross_pay: 281.78 },
      { id: '1e8d453d-ef1b-4804-9e93-c626939cf44f', gross_pay: 99.26 },
      { id: 'd975b467-a5c6-421c-a4e7-1d367cd85bf9', gross_pay: 303.96 },
      // Melvin - Wise payments
      { id: 'dfca2118-8794-4d77-bd4e-42b21b4caccb', gross_pay: 418.08 },
      { id: '30f5b62d-e732-448c-be81-91a805808d3c', gross_pay: 99.26 },
      { id: '83b688f0-76d5-4b35-9a4f-71154f5f03bd', gross_pay: 388.14 },
      { id: '99573266-58fe-4a01-8929-7ab2483e6174', gross_pay: 186.06 },
      // Stef - Wise payments (recent)
      { id: '99c35bd8-31bf-4da8-b9aa-f972b9542279', gross_pay: 1527.38 },
      { id: '7818dfdb-5c50-4335-bd57-03568ba88b90', gross_pay: 346.52 },
      { id: '79b6bd82-aa87-41e8-8d76-60739b81002e', gross_pay: 1913.37 },
      { id: '4881032f-3ec1-4481-a144-986f9af88deb', gross_pay: 1243.0 },
      // Stef - PayPal payments (historical)
      { id: 'f606b1ab-1c81-4bf2-8652-28738f6b6720', gross_pay: 781.75 },   // 250407-250420
      { id: 'b697d3f4-4a86-40ea-9e39-84ba2be4078b', gross_pay: 1765.75 },  // 250324-250406
      { id: 'd04c1497-098b-4525-897f-70eb13934ca9', gross_pay: 1825.00 },  // 250310-250323
      { id: 'e5307a98-1f37-4d3c-80b3-2d3c52269ea5', gross_pay: 987.75 },   // 250224-250309
      { id: 'd1a7c5c1-39d0-4eda-a06c-05cdb81fff72', gross_pay: 98.00 },    // 250210-250223
      { id: 'e9c53f6d-300c-4c91-ab26-0984435bcac9', gross_pay: 60.50 },    // 250127-250209
      { id: '328bba7a-28b9-4edf-84a8-17dfadc9e5a9', gross_pay: 316.75 },   // 250113-250126
      { id: '99f06876-cc2c-438d-90a4-84f2aa538056', gross_pay: 429.25 },   // 241216-241229
    ];

    try {
      let updated = 0;
      for (const fix of recordFixes) {
        const encrypted = CryptoJS.AES.encrypt(fix.gross_pay.toString(), _k).toString();
        const { error } = await supabase
          .from('employee_payroll')
          .update({ gross_pay: encrypted })
          .eq('id', fix.id);

        if (error) {
          console.error(`Failed to update ${fix.id}:`, error);
        } else {
          updated++;
        }
      }

      console.log(`Updated ${updated}/${recordFixes.length} contractor payroll records`);
      return { success: true, updated };
    } catch (error) {
      console.error('Error fixing contractor payroll:', error);
      return { success: false, error: error.message };
    }
  }

  // Expose functions to window for browser console access
  static initConsoleHelpers() {
    if (typeof window !== 'undefined') {
      window.fixContractorPayroll = () => ProfitAnalysisService.fixContractorPayroll();

      // Quarterly rate snapshot functions
      window.generateAllSnapshots = () => ProfitAnalysisService.generateAllQuarterlySnapshots();
      window.generateSnapshot = (start, end) => ProfitAnalysisService.generateQuarterlySnapshot(start, end);
      window.backfillCosts = (start, end) => ProfitAnalysisService.backfillLaborCosts(start, end);
      window.lockQuarter = (start) => ProfitAnalysisService.lockQuarter(start);
      window.getSnapshots = () => ProfitAnalysisService.getQuarterlySnapshots();

      console.log('💰 Profit Analysis Console Helpers:');
      console.log('  generateAllSnapshots() - Generate rate snapshots for all quarters with payroll');
      console.log('  generateSnapshot("2025-01-01", "2025-03-31") - Generate for specific quarter');
      console.log('  backfillCosts("2024-01-01", "2025-12-31") - Backfill labor_cost on time entries');
      console.log('  lockQuarter("2025-01-01") - Lock a quarter\'s rates');
      console.log('  getSnapshots() - View all rate snapshots');
    }
  }

  // ============================================
  // Employee Compensation CRUD
  // ============================================

  /**
   * Fetch all compensation records with employee names
   */
  static async getAllCompensation() {
    try {
      const { data, error } = await supabase
        .from('employee_compensation')
        .select(`
          id,
          employee_id,
          hourly_rate,
          markup_percent,
          effective_date,
          created_at,
          created_by,
          notes,
          employees (
            id,
            name
          )
        `)
        .order('effective_date', { ascending: false });

      if (error) throw error;

      return data.map(record => ({
        id: record.id,
        employeeId: record.employee_id,
        employeeName: record.employees?.name || 'Unknown',
        hourlyRate: decryptValue(record.hourly_rate),
        markupPercent: record.markup_percent ? decryptValue(record.markup_percent) : 0,
        effectiveDate: record.effective_date,
        createdAt: record.created_at,
        createdBy: record.created_by,
        notes: record.notes
      }));
    } catch (error) {
      console.error('Error fetching compensation:', error);
      throw error;
    }
  }

  /**
   * Get current rate for each employee
   */
  static async getCurrentRates() {
    try {
      const allCompensation = await this.getAllCompensation();
      const today = new Date().toISOString().split('T')[0];

      // Group by employee and get most recent rate that's <= today
      const ratesByEmployee = {};
      for (const record of allCompensation) {
        if (record.effectiveDate <= today) {
          if (!ratesByEmployee[record.employeeId] ||
              record.effectiveDate > ratesByEmployee[record.employeeId].effectiveDate) {
            ratesByEmployee[record.employeeId] = record;
          }
        }
      }

      return Object.values(ratesByEmployee);
    } catch (error) {
      console.error('Error fetching current rates:', error);
      throw error;
    }
  }

  /**
   * Get compensation history for a specific employee
   */
  static async getCompensationByEmployee(employeeId) {
    try {
      const { data, error } = await supabase
        .from('employee_compensation')
        .select(`
          id,
          employee_id,
          hourly_rate,
          markup_percent,
          effective_date,
          created_at,
          created_by,
          notes
        `)
        .eq('employee_id', employeeId)
        .order('effective_date', { ascending: false });

      if (error) throw error;

      return data.map(record => ({
        id: record.id,
        employeeId: record.employee_id,
        hourlyRate: decryptValue(record.hourly_rate),
        markupPercent: record.markup_percent ? decryptValue(record.markup_percent) : 0,
        effectiveDate: record.effective_date,
        createdAt: record.created_at,
        createdBy: record.created_by,
        notes: record.notes
      }));
    } catch (error) {
      console.error('Error fetching employee compensation:', error);
      throw error;
    }
  }

  /**
   * Create a new compensation record
   */
  static async createCompensation({ employeeId, hourlyRate, markupPercent, effectiveDate, notes }) {
    try {
      const { data, error } = await supabase
        .from('employee_compensation')
        .insert({
          employee_id: employeeId,
          hourly_rate: encryptValue(hourlyRate),
          markup_percent: encryptValue(markupPercent || 0),
          effective_date: effectiveDate,
          notes: notes || null
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error creating compensation:', error);
      throw error;
    }
  }

  /**
   * Update an existing compensation record
   */
  static async updateCompensation(id, { hourlyRate, markupPercent, effectiveDate, notes }) {
    try {
      const { data, error } = await supabase
        .from('employee_compensation')
        .update({
          hourly_rate: encryptValue(hourlyRate),
          markup_percent: encryptValue(markupPercent || 0),
          effective_date: effectiveDate,
          notes: notes || null
        })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating compensation:', error);
      throw error;
    }
  }

  /**
   * Delete a compensation record
   */
  static async deleteCompensation(id) {
    try {
      const { error } = await supabase
        .from('employee_compensation')
        .delete()
        .eq('id', id);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error deleting compensation:', error);
      throw error;
    }
  }

  // ============================================
  // Employees (for dropdown selection)
  // ============================================

  /**
   * Get all active employees for dropdown
   */
  static async getEmployees() {
    try {
      const { data, error } = await supabase
        .from('employees')
        .select('id, name, is_contractor')
        .eq('is_active', true)
        .order('name');

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error fetching employees:', error);
      throw error;
    }
  }

  // ============================================
  // Employee Payroll CRUD
  // ============================================

  /**
   * Get all payroll records
   */
  static async getAllPayroll() {
    try {
      const { data, error } = await supabase
        .from('employee_payroll')
        .select(`
          id,
          employee_id,
          pay_period_start,
          pay_period_end,
          gross_pay,
          created_at,
          created_by,
          notes,
          employees (
            id,
            name,
            is_contractor
          )
        `)
        .order('pay_period_end', { ascending: false })
        .limit(5000); // Ensure we get all payroll records

      if (error) throw error;

      return data.map(record => ({
        id: record.id,
        employeeId: record.employee_id,
        employeeName: record.employees?.name || 'Unknown',
        isContractor: record.employees?.is_contractor || false,
        payPeriodStart: record.pay_period_start,
        payPeriodEnd: record.pay_period_end,
        grossPay: decryptValue(record.gross_pay),
        createdAt: record.created_at,
        createdBy: record.created_by,
        notes: record.notes
      }));
    } catch (error) {
      console.error('Error fetching payroll:', error);
      throw error;
    }
  }

  /**
   * Get payroll for a specific employee
   */
  static async getPayrollByEmployee(employeeId) {
    try {
      const { data, error } = await supabase
        .from('employee_payroll')
        .select(`
          id,
          employee_id,
          pay_period_start,
          pay_period_end,
          gross_pay,
          created_at,
          created_by,
          notes
        `)
        .eq('employee_id', employeeId)
        .order('pay_period_end', { ascending: false });

      if (error) throw error;

      return data.map(record => ({
        id: record.id,
        employeeId: record.employee_id,
        payPeriodStart: record.pay_period_start,
        payPeriodEnd: record.pay_period_end,
        grossPay: decryptValue(record.gross_pay),
        createdAt: record.created_at,
        createdBy: record.created_by,
        notes: record.notes
      }));
    } catch (error) {
      console.error('Error fetching employee payroll:', error);
      throw error;
    }
  }

  /**
   * Create a new payroll record
   */
  static async createPayroll({ employeeId, payPeriodStart, payPeriodEnd, grossPay, notes }) {
    try {
      const { data, error } = await supabase
        .from('employee_payroll')
        .insert({
          employee_id: employeeId,
          pay_period_start: payPeriodStart,
          pay_period_end: payPeriodEnd,
          gross_pay: encryptValue(grossPay),
          notes: notes || null
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error creating payroll:', error);
      throw error;
    }
  }

  /**
   * Update an existing payroll record
   */
  static async updatePayroll(id, { payPeriodStart, payPeriodEnd, grossPay, notes }) {
    try {
      const { data, error } = await supabase
        .from('employee_payroll')
        .update({
          pay_period_start: payPeriodStart,
          pay_period_end: payPeriodEnd,
          gross_pay: encryptValue(grossPay),
          notes: notes || null
        })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating payroll:', error);
      throw error;
    }
  }

  /**
   * Delete a payroll record
   */
  static async deletePayroll(id) {
    try {
      const { error } = await supabase
        .from('employee_payroll')
        .delete()
        .eq('id', id);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error deleting payroll:', error);
      throw error;
    }
  }

  /**
   * Import payroll from CSV data (Gusto PayrollDetails format)
   * @param {Array} csvRows - Parsed CSV rows
   * @param {Array} employees - Employee list for name matching
   * @returns {Object} { imported: number, skipped: number, errors: string[] }
   */
  static async importPayrollFromCSV(csvRows, employees) {
    const results = { imported: 0, skipped: 0, errors: [] };

    // Build employee name lookup (handle first/last name matching)
    const employeeLookup = {};
    for (const emp of employees) {
      employeeLookup[emp.name.toLowerCase()] = emp.id;
      // Also index by last name for flexible matching
      const parts = emp.name.split(' ');
      if (parts.length >= 2) {
        const lastName = parts[parts.length - 1].toLowerCase();
        employeeLookup[lastName] = emp.id;
      }
    }

    for (const row of csvRows) {
      try {
        // Skip voided paychecks
        if (row.source === 'Voided Paycheck' || row.source === 'Voided') {
          results.skipped++;
          continue;
        }

        // Parse employee name
        const firstName = (row.employeeFirstName || '').trim();
        const lastName = (row.employeeLastName || '').trim();
        const fullName = `${firstName} ${lastName}`.toLowerCase().trim();

        // Find employee ID
        let employeeId = employeeLookup[fullName] || employeeLookup[lastName.toLowerCase()];
        if (!employeeId) {
          results.errors.push(`Employee not found: ${firstName} ${lastName}`);
          results.skipped++;
          continue;
        }

        // Parse pay period dates (format: "12/22/2024 - 1/4/2025")
        const periodParts = (row.payPeriod || '').split(' - ');
        if (periodParts.length !== 2) {
          results.errors.push(`Invalid pay period format: ${row.payPeriod}`);
          results.skipped++;
          continue;
        }

        const parseDate = (dateStr) => {
          const [month, day, year] = dateStr.trim().split('/');
          return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
        };

        const payPeriodStart = parseDate(periodParts[0]);
        const payPeriodEnd = parseDate(periodParts[1]);

        // Parse monetary values (remove $ and commas)
        const parseMoney = (val) => {
          if (!val) return 0;
          return parseFloat(val.toString().replace(/[$,]/g, '')) || 0;
        };

        // Calculate total cost (gross + employer taxes)
        const grossPay = parseMoney(row.grossPay);
        const employerMedicare = parseMoney(row.employerMedicareTax);
        const employerSS = parseMoney(row.employerSocialSecurity);
        const stateUnemployment = parseMoney(row.stateUnemployment || row.caUnemployment || row.txUnemployment || 0);

        const totalCost = grossPay + employerMedicare + employerSS + stateUnemployment;

        if (totalCost <= 0) {
          results.skipped++;
          continue;
        }

        // Insert record
        await this.createPayroll({
          employeeId,
          payPeriodStart,
          payPeriodEnd,
          grossPay: totalCost,
          notes: `Imported from Gusto CSV. Gross: $${grossPay.toFixed(2)}, Employer taxes: $${(totalCost - grossPay).toFixed(2)}`
        });

        results.imported++;
      } catch (err) {
        // Handle duplicate constraint violation
        if (err.message.includes('unique_employee_payroll_period') || err.code === '23505') {
          results.skipped++;
        } else {
          results.errors.push(`Row error: ${err.message}`);
          results.skipped++;
        }
      }
    }

    return results;
  }

  // ============================================
  // Billable Hours & True Cost Calculation
  // ============================================

  /**
   * Get billable hours by employee for a date range
   * Billable = ALL numeric tags EXCEPT overhead tags (200-208)
   * This includes project work, remote support, and any other revenue-generating activity
   * Returns: { [employeeId]: totalHours }
   */
  static async getBillableHours(startDate, endDate) {
    try {
      // Fetch all rows using employee_id FK - Supabase defaults to 1000 limit
      const { data, error } = await supabase
        .from('manictime_tags')
        .select('employee_id, project_id, duration_hours')
        .gte('start_time', startDate)
        .lte('start_time', endDate)
        .not('employee_id', 'is', null)
        .not('project_id', 'is', null)
        .limit(50000); // Ensure we get all rows for 6 months of data

      if (error) throw error;

      // Filter to billable entries and sum by employee_id
      const hoursByEmployeeId = {};

      for (const entry of data) {
        const hours = parseFloat(entry.duration_hours) || 0;
        const employeeId = entry.employee_id;

        hoursByEmployeeId[employeeId] = (hoursByEmployeeId[employeeId] || 0) + hours;
      }

      return hoursByEmployeeId;
    } catch (error) {
      console.error('Error fetching billable hours:', error);
      throw error;
    }
  }

  /**
   * Calculate true cost per employee using a rolling 26-week window.
   * Uses employee_id FK for reliable matching
   */
  static async calculateTrueCosts() {
    try {
      // Calculate date range (rolling 26 weeks from today)
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - (this.RATE_WINDOW_WEEKS * 7) + 1);

      const startDateStr = startDate.toISOString().split('T')[0];
      const endDateStr = endDate.toISOString().split('T')[0];

      return this.calculateTrueCostsForDateRange(startDateStr, endDateStr);
    } catch (error) {
      console.error('Error calculating true costs:', error);
      throw error;
    }
  }

  /**
   * Calculate true cost per employee for a specific date range
   * Used for display in Labor Costs tab
   *
   * Calculation methods:
   * - Heath: Flat $30/hr
   * - Austin: Special calculation (see calculateTrueCostsFromData)
   * - Everyone else: Salary / Project Hours (absorbs overhead into rate)
   */
  static async calculateTrueCostsForDateRange(startDateStr, endDateStr) {
    try {
      // Get payroll data for the period
      const allPayroll = await this.getAllPayroll();
      const payrollInRange = allPayroll.filter(p =>
        p.payPeriodEnd >= startDateStr && p.payPeriodStart <= endDateStr
      );

      // Sum payroll by employee_id (skip NaN values from decryption failures)
      const payrollByEmployeeId = {};
      for (const record of payrollInRange) {
        if (isNaN(record.grossPay)) continue;

        if (!payrollByEmployeeId[record.employeeId]) {
          payrollByEmployeeId[record.employeeId] = {
            totalGrossPay: 0,
            periodCount: 0
          };
        }
        payrollByEmployeeId[record.employeeId].totalGrossPay += record.grossPay;
        payrollByEmployeeId[record.employeeId].periodCount++;
      }

      // Get ALL hours data for the period (needed for Austin's calculation and hours breakdown)
      const allHoursData = await this.getAllHoursData();
      const hoursBreakdown = this.calculateHoursBreakdownForRange(allHoursData, startDateStr, endDateStr);

      // Get current rates for comparison
      const currentRates = await this.getCurrentRates();
      const ratesByEmployeeId = {};
      for (const rate of currentRates) {
        ratesByEmployeeId[rate.employeeId] = rate;
      }

      // HR payroll sync writes the normalized rate into the current quarter.
      // Prefer that rate for the current window; otherwise use the legacy
      // payroll fallback below.
      const quarterDate = new Date(`${endDateStr}T00:00:00`);
      const currentQuarterStart = new Date(
        quarterDate.getFullYear(),
        Math.floor(quarterDate.getMonth() / 3) * 3,
        1
      ).toISOString().split('T')[0];
      const snapshots = await this.getQuarterlySnapshots().catch(() => []);
      const syncedSnapshotByEmployeeId = {};
      for (const snapshot of snapshots) {
        if (
          snapshot.quarter_start === currentQuarterStart &&
          !syncedSnapshotByEmployeeId[snapshot.employee_id]
        ) {
          syncedSnapshotByEmployeeId[snapshot.employee_id] = snapshot;
        }
      }

      // Calculate true cost per employee
      const employees = await this.getEmployees();
      const results = [];

      for (const emp of employees) {
        const payrollData = payrollByEmployeeId[emp.id] || { totalGrossPay: 0, periodCount: 0 };
        const hours = hoursBreakdown[emp.id] || { projectHours: 0, allHours: 0, overheadHours: 0, modelingHours: 0 };
        const currentRate = ratesByEmployeeId[emp.id];
        const syncedSnapshot = syncedSnapshotByEmployeeId[emp.id];

        let trueCostPerHour = null;
        let calculationMethod = '26-week payroll / project-assigned hours';
        let displayedGrossPay = payrollData.totalGrossPay;
        let displayedProjectHours = hours.projectHours;

        // Preferred: normalized rate synced from HR after a Patriot upload.
        if (syncedSnapshot && parseFloat(syncedSnapshot.calculated_rate) > 0) {
          trueCostPerHour = parseFloat(syncedSnapshot.calculated_rate);
          displayedGrossPay = parseFloat(syncedSnapshot.total_gross_pay) || 0;
          displayedProjectHours = parseFloat(syncedSnapshot.total_project_hours) || 0;
          calculationMethod = 'HR payroll + 26-week utilization + 5 hr/week allowance';
        }
        // Fallback until an HR-synced snapshot exists.
        else if (hours.projectHours > 0 && payrollData.totalGrossPay > 0) {
          const payrollWithWorkersComp =
            payrollData.totalGrossPay * (1 + this.WORKERS_COMP_RATE);
          trueCostPerHour =
            (payrollWithWorkersComp * this.RECOVERABLE_PAYROLL_SHARE) /
            hours.projectHours;
        }

        results.push({
          employeeId: emp.id,
          employeeName: emp.name,
          isContractor: emp.is_contractor || false,
          totalGrossPay: Math.round(displayedGrossPay * 100) / 100,
          totalProjectHours: Math.round(displayedProjectHours * 100) / 100,
          totalModelingHours: Math.round(hours.modelingHours * 100) / 100,
          totalOverheadHours: Math.round(hours.overheadHours * 100) / 100,
          totalAllHours: Math.round(hours.allHours * 100) / 100,
          trueCostPerHour: trueCostPerHour !== null ? Math.round(trueCostPerHour * 100) / 100 : null,
          calculationMethod,
          baseRate: currentRate?.hourlyRate || null,
          markupPercent: currentRate?.markupPercent || 0,
          hasPayrollData: payrollData.periodCount > 0,
          hasHoursData: hours.projectHours > 0 || hours.allHours > 0,
          periodCount: payrollData.periodCount,
          dateRange: { start: startDateStr, end: endDateStr }
        });
      }

      return results.sort((a, b) => a.employeeName.localeCompare(b.employeeName));
    } catch (error) {
      console.error('Error calculating true costs for date range:', error);
      throw error;
    }
  }

  /**
   * Get the 26-week window ending at a given date
   * Returns { startDate, endDate } as YYYY-MM-DD strings
   */
  static getSixMonthWindowForDate(projectDate) {
    const endDate = new Date(projectDate);
    const startDate = new Date(projectDate);
    startDate.setDate(startDate.getDate() - (this.RATE_WINDOW_WEEKS * 7) + 1);

    return {
      startDate: startDate.toISOString().split('T')[0],
      endDate: endDate.toISOString().split('T')[0]
    };
  }

  /**
   * Get ALL hours data at once (for batch processing)
   * Returns raw data including overhead tags (needed for Austin's calculation)
   */
  static async getAllHoursData() {
    try {
      const { data, error } = await supabase
        .from('manictime_tags')
        .select('employee_id, project_id, tag_name, duration_hours, start_time')
        .not('employee_id', 'is', null)
        .limit(100000);

      if (error) throw error;

      return data;
    } catch (error) {
      console.error('Error fetching all hours data:', error);
      throw error;
    }
  }

  /**
   * Calculate project hours for a date range (excludes overhead)
   * For most employees: This is what we divide salary by to get true cost
   */
  static calculateProjectHoursForRange(allHoursData, startDate, endDate, employeeId = null) {
    let totalHours = 0;

    for (const entry of allHoursData) {
      const entryDate = entry.start_time?.split('T')[0];
      if (!entryDate || entryDate < startDate || entryDate > endDate) continue;
      if (employeeId !== null && entry.employee_id !== employeeId) continue;

      if (!entry.project_id) continue;

      totalHours += parseFloat(entry.duration_hours) || 0;
    }

    return totalHours;
  }

  /**
   * Calculate hours by employee for a date range (for true cost calculations)
   * Returns: { [employeeId]: { projectHours, allHours, overheadHours, modelingHours } }
   *
   * projectHours excludes:
   * - General overhead (200-208)
   * - Matt's management hours (400, 401)
   */
  static calculateHoursBreakdownForRange(allHoursData, startDate, endDate) {
    const breakdown = {};

    for (const entry of allHoursData) {
      const entryDate = entry.start_time?.split('T')[0];
      if (!entryDate || entryDate < startDate || entryDate > endDate) continue;

      const hours = parseFloat(entry.duration_hours) || 0;
      const employeeId = entry.employee_id;

      if (!breakdown[employeeId]) {
        breakdown[employeeId] = { projectHours: 0, allHours: 0, overheadHours: 0, modelingHours: 0 };
      }

      breakdown[employeeId].allHours += hours;

      if (!entry.project_id) {
        breakdown[employeeId].overheadHours += hours;
      } else {
        breakdown[employeeId].projectHours += hours;
        breakdown[employeeId].modelingHours += hours;
      }
    }

    return breakdown;
  }

  /**
   * Calculate true cost per employee for a date range from pre-fetched data
   * Returns: { [employeeId]: trueCostPerHour }
   *
   * Uses one standard fallback calculation for every employee.
   */
  static calculateTrueCostsFromData(allPayroll, allHoursData, startDate, endDate) {
    // Filter payroll to date range
    const payrollInRange = allPayroll.filter(p =>
      p.payPeriodEnd >= startDate && p.payPeriodStart <= endDate
    );

    // Sum payroll by employee_id
    const payrollByEmployeeId = {};
    for (const record of payrollInRange) {
      if (isNaN(record.grossPay)) continue;
      if (!payrollByEmployeeId[record.employeeId]) {
        payrollByEmployeeId[record.employeeId] = 0;
      }
      payrollByEmployeeId[record.employeeId] += record.grossPay;
    }

    // Calculate hours breakdown for this range
    const hoursBreakdown = this.calculateHoursBreakdownForRange(allHoursData, startDate, endDate);

    // Calculate true cost per employee
    const trueCostByEmployeeId = {};
    const allEmployeeIds = new Set([
      ...Object.keys(payrollByEmployeeId),
      ...Object.keys(hoursBreakdown)
    ]);

    for (const empIdStr of allEmployeeIds) {
      const empId = parseInt(empIdStr);
      const pay = payrollByEmployeeId[empId] || 0;
      const hours = hoursBreakdown[empId] || { projectHours: 0, allHours: 0, overheadHours: 0, modelingHours: 0 };

      // Recover 35 of 40 weekly hours through project work.
      if (hours.projectHours > 0 && pay > 0) {
        trueCostByEmployeeId[empId] =
          (pay * (1 + this.WORKERS_COMP_RATE) * this.RECOVERABLE_PAYROLL_SHARE) /
          hours.projectHours;
      }
    }

    return trueCostByEmployeeId;
  }

  // ============================================
  // Profit Analysis Calculations
  // ============================================

  /**
   * Get all project profit data - uses True Cost (payroll / billable hours)
   * True Cost = Total Gross Pay / Total Billable Hours over the 6 months PRECEDING each project's completion date
   * This gives accurate historical profit margins based on what costs were when the project was done
   */
  static async getProjectProfitData() {
    try {
      // 1. Fetch all projects (including those without model_price)
      const { data: projects, error: projectsError } = await supabase
        .from('projects')
        .select(`
          id,
          job_number,
          rev,
          name,
          client_id,
          us_state,
          approximate_acreage,
          template_filter,
          initial_acceptance,
          invoiced_date,
          billing_status,
          modeling_status,
          model_price,
          discount_percentage,
          tn_revenue,
          clients (name)
        `);

      if (projectsError) throw projectsError;

      // 2. Fetch time entries with stored labor_cost (from quarterly rate snapshots)
      // Include employee names and start_time for breakdown display and rate calculation
      const { data: timeEntries, error: timeError } = await supabase
        .from('manictime_tags')
        .select(`
          project_id,
          employee_id,
          duration_hours,
          labor_cost,
          is_billable,
          hourly_rate_used,
          start_time,
          employees (id, name)
        `);

      if (timeError) throw timeError;

      // 2b. Fetch rate snapshots for calculating costs when labor_cost is null
      const { data: rateSnapshots, error: snapshotError } = await supabase
        .from('employee_rate_snapshots')
        .select('employee_id, quarter_start, calculated_rate');

      if (snapshotError) throw snapshotError;

      // Build lookup: employee_id -> { quarterStart -> rate }
      // Also track sorted quarters per employee for fallback
      const rateLookup = {};
      const employeeQuarters = {};
      for (const s of rateSnapshots) {
        if (!rateLookup[s.employee_id]) {
          rateLookup[s.employee_id] = {};
          employeeQuarters[s.employee_id] = [];
        }
        rateLookup[s.employee_id][s.quarter_start] = s.calculated_rate;
        employeeQuarters[s.employee_id].push(s.quarter_start);
      }
      // Sort quarters descending for fallback
      for (const empId in employeeQuarters) {
        employeeQuarters[empId].sort((a, b) => b.localeCompare(a));
      }

      // Helper to get previous quarter date string
      const getPrevQuarter = (qStart) => {
        const d = new Date(qStart);
        d.setMonth(d.getMonth() - 3);
        return d.toISOString().split('T')[0];
      };

      // Helper to get rate for an employee at a given date with smart fallback
      // If current quarter rate is missing or suspiciously low, use previous quarter
      const getRateForEntry = (employeeId, startTime) => {
        if (!startTime || !rateLookup[employeeId]) return null;
        const entryDate = new Date(startTime);
        const qStart = new Date(entryDate.getFullYear(), Math.floor(entryDate.getMonth() / 3) * 3, 1)
          .toISOString().split('T')[0];

        const currentRate = rateLookup[employeeId][qStart];
        const prevQStart = getPrevQuarter(qStart);
        const prevRate = rateLookup[employeeId][prevQStart];

        // Use current rate if it exists and is reasonable
        if (currentRate && currentRate > 0) {
          // If previous rate exists and current is < 50% of previous, it's likely incomplete data
          if (prevRate && currentRate < prevRate * 0.5) {
            return prevRate;
          }
          return currentRate;
        }

        // Fallback to previous quarter
        if (prevRate && prevRate > 0) {
          return prevRate;
        }

        // Last resort: use most recent available rate for this employee
        const quarters = employeeQuarters[employeeId] || [];
        for (const q of quarters) {
          const rate = rateLookup[employeeId][q];
          if (rate && rate > 0) return rate;
        }

        return null;
      };

      // 3. Group time entries by project and sum hours + labor_cost
      const projectStats = {};
      for (const entry of timeEntries) {
        if (!entry.project_id) continue;
        if (!projectStats[entry.project_id]) {
          projectStats[entry.project_id] = { hours: 0, laborCost: 0 };
        }
        const hours = parseFloat(entry.duration_hours) || 0;
        projectStats[entry.project_id].hours += hours;

        // Use stored labor_cost, or calculate from rate snapshots if missing
        let cost = parseFloat(entry.labor_cost) || 0;
        if (!entry.labor_cost && entry.employee_id) {
          const rate = getRateForEntry(entry.employee_id, entry.start_time);
          if (rate) cost = hours * rate;
        }
        projectStats[entry.project_id].laborCost += cost;
      }

      return projects.map(p => {
        // Use tn_revenue (what TN actually earns) for profit calculations
        const revenue = parseFloat(p.tn_revenue ?? p.model_price) || 0;
        const stats = projectStats[p.id] || { hours: 0, laborCost: 0 };
        const laborHours = stats.hours;
        const laborCost = stats.laborCost;

        const profit = revenue - laborCost;
        const marginPercent = revenue > 0 ? (profit / revenue) * 100 : 0;

        return {
          projectId: p.id,
          jobNumber: p.job_number,
          rev: p.rev?.toString() || '0',
          projectName: p.name,
          clientName: p.clients?.name || null,
          clientId: p.client_id,
          usState: normalizeState(p.us_state),
          acreage: p.approximate_acreage,
          templateFilter: p.template_filter || [],
          initialAcceptance: p.initial_acceptance,
          invoicedDate: p.invoiced_date,
          billingStatus: p.billing_status,
          modelingStatus: p.modeling_status,
          revenue,
          modelPrice: p.model_price,
          tnRevenue: p.tn_revenue,
          discountPercentage: p.discount_percentage || 0,
          laborHours: Math.round(laborHours * 100) / 100,
          laborCost: Math.round(laborCost * 100) / 100,
          profit: Math.round(profit * 100) / 100,
          marginPercent: Math.round(marginPercent * 10) / 10,
          // Store time entries for date-based labor cost calculation and employee breakdown
          timeEntries: timeEntries
            .filter(e => e.project_id === p.id)
            .map(e => {
              const hours = parseFloat(e.duration_hours) || 0;
              // Calculate rate and cost if not stored
              let rate = e.hourly_rate_used;
              let cost = parseFloat(e.labor_cost) || 0;
              if (!e.labor_cost && e.employee_id) {
                rate = getRateForEntry(e.employee_id, e.start_time);
                if (rate) cost = hours * rate;
              }
              return {
                ...e,
                employeeName: e.employees?.name || 'Unknown',
                hourly_rate_used: rate,
                labor_cost: cost
              };
            })
        };
      });
    } catch (error) {
      console.error('Error fetching project profits:', error);
      throw error;
    }
  }

  /**
   * Get hours + labor cost for a single project.
   * Uses same rate-resolution logic as getProjectProfitData() but only
   * fetches data for the given project (1-2 queries instead of 3 full-table queries).
   * Returns { hours, laborCost } rounded to 2 decimal places.
   */
  static async getProjectCostSummary(projectId) {
    try {
      // 1. Fetch time entries for this project only
      const { data: timeEntries, error: timeError } = await supabase
        .from('manictime_tags')
        .select('employee_id, duration_hours, labor_cost, hourly_rate_used, start_time')
        .eq('project_id', projectId);

      if (timeError) throw timeError;
      if (!timeEntries || timeEntries.length === 0) {
        return { hours: 0, laborCost: 0 };
      }

      // 2. Check if any entries are missing labor_cost
      const missingCostEntries = timeEntries.filter(e => !e.labor_cost && e.employee_id);
      let getRateForEntry = () => null;

      if (missingCostEntries.length > 0) {
        // Only fetch rate snapshots if needed, scoped to relevant employees
        const employeeIds = [...new Set(missingCostEntries.map(e => e.employee_id))];
        const { data: rateSnapshots, error: snapshotError } = await supabase
          .from('employee_rate_snapshots')
          .select('employee_id, quarter_start, calculated_rate')
          .in('employee_id', employeeIds);

        if (snapshotError) throw snapshotError;

        // Build lookup: employee_id -> { quarterStart -> rate }
        const rateLookup = {};
        const employeeQuarters = {};
        for (const s of (rateSnapshots || [])) {
          if (!rateLookup[s.employee_id]) {
            rateLookup[s.employee_id] = {};
            employeeQuarters[s.employee_id] = [];
          }
          rateLookup[s.employee_id][s.quarter_start] = s.calculated_rate;
          employeeQuarters[s.employee_id].push(s.quarter_start);
        }
        for (const empId in employeeQuarters) {
          employeeQuarters[empId].sort((a, b) => b.localeCompare(a));
        }

        const getPrevQuarter = (qStart) => {
          const d = new Date(qStart);
          d.setMonth(d.getMonth() - 3);
          return d.toISOString().split('T')[0];
        };

        getRateForEntry = (employeeId, startTime) => {
          if (!startTime || !rateLookup[employeeId]) return null;
          const entryDate = new Date(startTime);
          const qStart = new Date(entryDate.getFullYear(), Math.floor(entryDate.getMonth() / 3) * 3, 1)
            .toISOString().split('T')[0];

          const currentRate = rateLookup[employeeId][qStart];
          const prevQStart = getPrevQuarter(qStart);
          const prevRate = rateLookup[employeeId][prevQStart];

          if (currentRate && currentRate > 0) {
            if (prevRate && currentRate < prevRate * 0.5) {
              return prevRate;
            }
            return currentRate;
          }
          if (prevRate && prevRate > 0) return prevRate;

          const quarters = employeeQuarters[employeeId] || [];
          for (const q of quarters) {
            const rate = rateLookup[employeeId][q];
            if (rate && rate > 0) return rate;
          }
          return null;
        };
      }

      // 3. Sum hours and labor cost
      let totalHours = 0;
      let totalCost = 0;
      for (const entry of timeEntries) {
        const hours = parseFloat(entry.duration_hours) || 0;
        totalHours += hours;

        let cost = parseFloat(entry.labor_cost) || 0;
        if (!entry.labor_cost && entry.employee_id) {
          const rate = getRateForEntry(entry.employee_id, entry.start_time);
          if (rate) cost = hours * rate;
        }
        totalCost += cost;
      }

      return {
        hours: Math.round(totalHours * 100) / 100,
        laborCost: Math.round(totalCost * 100) / 100
      };
    } catch (error) {
      console.error('Error fetching project cost summary:', error);
      throw error;
    }
  }

  /**
   * Get template type profitability - calculated from project data
   * Uses even-split attribution for multi-category projects
   */
  static calculateTemplateProfitability(projects) {
    const templateStats = {};

    for (const project of projects) {
      const templates = project.templateFilter || [];
      if (templates.length === 0) continue;

      // Even-split attribution
      const splitFactor = templates.length;

      for (const template of templates) {
        if (!templateStats[template]) {
          templateStats[template] = {
            templateType: template,
            projectIds: new Set(),
            revenue: 0,
            laborCost: 0,
            profit: 0,
            marginSum: 0,
            marginCount: 0
          };
        }

        templateStats[template].projectIds.add(project.projectId);
        templateStats[template].revenue += project.revenue / splitFactor;
        templateStats[template].laborCost += project.laborCost / splitFactor;
        templateStats[template].profit += project.profit / splitFactor;
        templateStats[template].marginSum += project.marginPercent;
        templateStats[template].marginCount += 1;
      }
    }

    return Object.values(templateStats)
      .map(t => ({
        templateType: t.templateType,
        projectCount: t.projectIds.size,
        revenue: Math.round(t.revenue * 100) / 100,
        laborCost: Math.round(t.laborCost * 100) / 100,
        profit: Math.round(t.profit * 100) / 100,
        avgMarginPercent: t.marginCount > 0
          ? Math.round((t.marginSum / t.marginCount) * 10) / 10
          : 0
      }))
      .sort((a, b) => b.profit - a.profit);
  }

  /**
   * Get client profitability - calculated from project data
   */
  static calculateClientProfitability(projects) {
    const clientStats = {};

    for (const project of projects) {
      if (!project.clientId || !project.clientName) continue;

      if (!clientStats[project.clientId]) {
        clientStats[project.clientId] = {
          clientId: project.clientId,
          clientName: project.clientName,
          projectCount: 0,
          revenue: 0,
          laborCost: 0,
          profit: 0,
          marginSum: 0
        };
      }

      clientStats[project.clientId].projectCount += 1;
      clientStats[project.clientId].revenue += project.revenue;
      clientStats[project.clientId].laborCost += project.laborCost;
      clientStats[project.clientId].profit += project.profit;
      clientStats[project.clientId].marginSum += project.marginPercent;
    }

    return Object.values(clientStats)
      .map(c => ({
        clientId: c.clientId,
        clientName: c.clientName,
        projectCount: c.projectCount,
        revenue: Math.round(c.revenue * 100) / 100,
        laborCost: Math.round(c.laborCost * 100) / 100,
        profit: Math.round(c.profit * 100) / 100,
        avgMarginPercent: c.projectCount > 0
          ? Math.round((c.marginSum / c.projectCount) * 10) / 10
          : 0
      }))
      .sort((a, b) => b.profit - a.profit);
  }

  /**
   * Calculate summary metrics from project data
   */
  static calculateSummary(projects) {
    if (!projects || projects.length === 0) {
      return {
        totalRevenue: 0,
        totalLaborCost: 0,
        totalProfit: 0,
        overallMargin: 0,
        projectCount: 0,
        totalHours: 0
      };
    }

    const totalRevenue = projects.reduce((sum, p) => sum + p.revenue, 0);
    const totalLaborCost = projects.reduce((sum, p) => sum + p.laborCost, 0);
    const totalProfit = totalRevenue - totalLaborCost;
    const overallMargin = totalRevenue > 0 ? (totalProfit / totalRevenue) * 100 : 0;
    const totalHours = projects.reduce((sum, p) => sum + p.laborHours, 0);

    return {
      totalRevenue,
      totalLaborCost,
      totalProfit,
      overallMargin: Math.round(overallMargin * 10) / 10,
      projectCount: projects.length,
      totalHours: Math.round(totalHours * 10) / 10
    };
  }

  /**
   * Calculate overview metrics based on date range:
   * - Revenue: Sum of model_price for projects where invoiced_date is within date range
   * - Labor Cost: True cost for the SAME date range (Salary / Project Hours × Hours = Salary)
   *
   * The key insight: For a given date range, Labor Cost = Total Salary Paid
   * Because: Hours × (Salary / Hours) = Salary
   *
   * @param {Array} allProjects - All project data (with timeEntries included)
   * @param {Object} filters - Filter criteria including startDate and endDate
   * @param {Array} allPayroll - Pre-fetched payroll data
   * @param {Array} allHoursData - Pre-fetched hours data for true cost calculation
   */
  static calculateOverviewMetrics(allProjects, filters, allPayroll, allHoursData) {
    const { startDate, endDate } = filters;

    // Revenue: Sum model_price for projects with invoiced_date in range
    let totalRevenue = 0;
    let invoicedProjectCount = 0;

    for (const project of allProjects) {
      // Skip if doesn't match non-date filters
      if (!this.matchesNonDateFilters(project, filters)) continue;

      // Check if invoiced_date is within range
      if (project.invoicedDate) {
        const inRange =
          (!startDate || project.invoicedDate >= startDate) &&
          (!endDate || project.invoicedDate <= endDate);

        if (inRange) {
          totalRevenue += project.revenue;
          invoicedProjectCount++;
        }
      }
    }

    // Calculate TRUE COST using the FILTER date range (not rolling 6-month)
    // This gives us: Salary in period / Project Hours in period = Rate per employee
    const trueCostByEmployeeId = this.calculateTrueCostsFromData(allPayroll, allHoursData, startDate, endDate);

    // Labor Cost: Sum hours × true cost rate for manictime entries in date range
    let totalLaborCost = 0;
    let totalHours = 0;

    for (const project of allProjects) {
      // Skip if doesn't match non-date filters
      if (!this.matchesNonDateFilters(project, filters)) continue;

      const timeEntries = project.timeEntries || [];

      for (const entry of timeEntries) {
        const entryDate = entry.start_time?.split('T')[0];
        if (!entryDate) continue;

        // Check if entry date is within filter range
        const inRange =
          (!startDate || entryDate >= startDate) &&
          (!endDate || entryDate <= endDate);

        if (!inRange) continue;

        const hours = parseFloat(entry.duration_hours) || 0;
        totalHours += hours;

        const trueCostPerHour = trueCostByEmployeeId[entry.employee_id] || 0;
        totalLaborCost += hours * trueCostPerHour;
      }
    }

    const totalProfit = totalRevenue - totalLaborCost;
    const overallMargin = totalRevenue > 0 ? (totalProfit / totalRevenue) * 100 : 0;

    return {
      totalRevenue: Math.round(totalRevenue * 100) / 100,
      totalLaborCost: Math.round(totalLaborCost * 100) / 100,
      totalProfit: Math.round(totalProfit * 100) / 100,
      overallMargin: Math.round(overallMargin * 10) / 10,
      projectCount: invoicedProjectCount,
      totalHours: Math.round(totalHours * 10) / 10
    };
  }

  /**
   * Check if a project matches non-date filters
   */
  static matchesNonDateFilters(project, filters) {
    // Search term filter
    if (filters.searchTerm && filters.searchTerm.trim()) {
      const term = filters.searchTerm.toLowerCase().trim();
      const matches =
        (project.jobNumber && project.jobNumber.toLowerCase().includes(term)) ||
        (project.projectName && project.projectName.toLowerCase().includes(term)) ||
        (project.clientName && project.clientName.toLowerCase().includes(term));
      if (!matches) return false;
    }

    // Template type filter
    if (filters.templateTypes && filters.templateTypes.length > 0) {
      if (!project.templateFilter || !project.templateFilter.some(t => filters.templateTypes.includes(t))) {
        return false;
      }
    }

    // Client filter
    if (filters.clientIds && filters.clientIds.length > 0) {
      if (!filters.clientIds.includes(project.clientId)) return false;
    }

    // State filter
    if (filters.states && filters.states.length > 0) {
      if (!filters.states.includes(project.usState)) return false;
    }

    // Acreage range filter
    if (filters.acreageRanges && filters.acreageRanges.length > 0) {
      if (project.acreage == null) return false;
      const inRange = filters.acreageRanges.some(rangeLabel => {
        const range = ACREAGE_RANGES.find(r => r.label === rangeLabel);
        if (!range) return false;
        return project.acreage >= range.min && project.acreage < range.max;
      });
      if (!inRange) return false;
    }

    // Billing status filter
    if (filters.billingStatuses && filters.billingStatuses.length > 0) {
      if (!filters.billingStatuses.includes(project.billingStatus)) return false;
    }

    return true;
  }

  /**
   * Filter projects by criteria
   */
  static filterProjects(projects, filters) {
    let filtered = [...projects];

    // Search term filter (job number, project name, client name)
    if (filters.searchTerm && filters.searchTerm.trim()) {
      const term = filters.searchTerm.toLowerCase().trim();
      filtered = filtered.filter(p =>
        (p.jobNumber && p.jobNumber.toLowerCase().includes(term)) ||
        (p.projectName && p.projectName.toLowerCase().includes(term)) ||
        (p.clientName && p.clientName.toLowerCase().includes(term))
      );
    }

    // Date range filter
    if (filters.startDate) {
      filtered = filtered.filter(p =>
        p.initialAcceptance && p.initialAcceptance >= filters.startDate
      );
    }
    if (filters.endDate) {
      filtered = filtered.filter(p =>
        p.initialAcceptance && p.initialAcceptance <= filters.endDate
      );
    }

    // Template type filter
    if (filters.templateTypes && filters.templateTypes.length > 0) {
      filtered = filtered.filter(p =>
        p.templateFilter && p.templateFilter.some(t => filters.templateTypes.includes(t))
      );
    }

    // Client filter
    if (filters.clientIds && filters.clientIds.length > 0) {
      filtered = filtered.filter(p =>
        filters.clientIds.includes(p.clientId)
      );
    }

    // State filter
    if (filters.states && filters.states.length > 0) {
      filtered = filtered.filter(p =>
        filters.states.includes(p.usState)
      );
    }

    // Acreage range filter
    if (filters.acreageRanges && filters.acreageRanges.length > 0) {
      filtered = filtered.filter(p => {
        if (p.acreage == null) return false;
        return filters.acreageRanges.some(rangeLabel => {
          const range = ACREAGE_RANGES.find(r => r.label === rangeLabel);
          if (!range) return false;
          return p.acreage >= range.min && p.acreage < range.max;
        });
      });
    }

    // Billing status filter
    if (filters.billingStatuses && filters.billingStatuses.length > 0) {
      filtered = filtered.filter(p =>
        filters.billingStatuses.includes(p.billingStatus)
      );
    }

    // Modeling status filter
    if (filters.modelingStatuses && filters.modelingStatuses.length > 0) {
      filtered = filtered.filter(p =>
        filters.modelingStatuses.includes(p.modelingStatus)
      );
    }

    return filtered;
  }

  /**
   * Available billing status options
   */
  static BILLING_STATUSES = ['Paid', 'Invoiced', 'Quoted', 'Voided'];

  /**
   * Get unique billing statuses from projects
   */
  static getUniqueBillingStatuses(projects) {
    const statuses = new Set();
    projects.forEach(p => {
      if (p.billingStatus) {
        statuses.add(p.billingStatus);
      }
    });
    // Return in a logical order
    const order = ['Paid', 'Invoiced', 'Quoted', 'Voided'];
    return order.filter(s => statuses.has(s));
  }

  /**
   * Get unique modeling statuses from projects
   */
  static getUniqueModelingStatuses(projects) {
    const statuses = new Set();
    projects.forEach(p => {
      if (p.modelingStatus) {
        statuses.add(p.modelingStatus);
      }
    });
    const order = ['Completed', 'Data Prep', 'Modeling', 'Review', 'Corrections', 'Ready to Model', 'Startup'];
    return order.filter(s => statuses.has(s));
  }

  /**
   * Get unique template types from projects
   */
  static getUniqueTemplateTypes(projects) {
    const types = new Set();
    projects.forEach(p => {
      if (p.templateFilter) {
        p.templateFilter.forEach(t => types.add(t));
      }
    });
    return Array.from(types).sort();
  }

  /**
   * Get unique clients from projects
   */
  static getUniqueClients(projects) {
    const clients = new Map();
    projects.forEach(p => {
      if (p.clientId && p.clientName) {
        clients.set(p.clientId, p.clientName);
      }
    });
    return Array.from(clients.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }

  /**
   * Get unique states from projects
   */
  static getUniqueStates(projects) {
    const states = new Set();
    projects.forEach(p => {
      if (p.usState) {
        states.add(p.usState);
      }
    });
    return Array.from(states).sort();
  }

  /**
   * Get human-readable revision label
   */
  static getRevisionLabel(rev) {
    const revNum = parseInt(rev);
    if (!rev || revNum === 0) return 'Base';
    if (revNum === 999) return 'Erosion';
    return `Rev ${revNum}`;
  }

  /**
   * Export projects to CSV format
   */
  static exportToCSV(projects) {
    const headers = [
      'Job Number',
      'Revision',
      'Project Name',
      'Client',
      'State',
      'Initial Acceptance',
      'Revenue',
      'Hours',
      'Labor Cost',
      'Profit',
      'Margin %'
    ];

    const rows = projects.map(p => [
      p.jobNumber || '',
      this.getRevisionLabel(p.rev),
      p.projectName || '',
      p.clientName || '',
      p.usState || '',
      p.initialAcceptance || '',
      p.revenue.toFixed(2),
      p.laborHours.toFixed(2),
      p.laborCost.toFixed(2),
      p.profit.toFixed(2),
      p.marginPercent.toFixed(1)
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `profit_analysis_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  // ============================================
  // Quarterly Rate Snapshots
  // ============================================

  /**
   * Get all rate snapshots
   */
  static async getQuarterlySnapshots() {
    try {
      const { data, error } = await supabase
        .from('employee_rate_snapshots')
        .select(`
          id,
          employee_id,
          quarter_start,
          quarter_end,
          total_gross_pay,
          total_project_hours,
          calculated_rate,
          is_locked,
          created_at,
          updated_at,
          employees (id, name)
        `)
        .order('quarter_start', { ascending: false });

      if (error) throw error;
      return data.map(s => ({
        ...s,
        employeeName: s.employees?.name || 'Unknown'
      }));
    } catch (error) {
      console.error('Error fetching quarterly snapshots:', error);
      throw error;
    }
  }

  /**
   * Generate/refresh quarterly rate snapshots for all employees
   * @param {string} quarterStart - Start of quarter (YYYY-MM-DD)
   * @param {string} quarterEnd - End of quarter (YYYY-MM-DD)
   */
  static async generateQuarterlySnapshot(quarterStart, quarterEnd) {
    try {
      // Get all payroll in the quarter
      const allPayroll = await this.getAllPayroll();
      const quarterPayroll = allPayroll.filter(p =>
        p.payPeriodEnd >= quarterStart && p.payPeriodStart <= quarterEnd
      );

      // Get all hours in the quarter
      const { data: hoursData, error: hoursError } = await supabase
        .from('manictime_tags')
        .select('employee_id, user_display_name, project_id, tag_name, duration_hours, start_time')
        .gte('start_time', quarterStart)
        .lte('start_time', quarterEnd + 'T23:59:59')
        .not('project_id', 'is', null);

      if (hoursError) throw hoursError;

      // Build employee lookup
      const { data: employees } = await supabase.from('employees').select('id, name');
      const empByName = {};
      employees.forEach(e => { empByName[e.name] = e.id; });

      // Group payroll by employee
      const payrollByEmployee = {};
      for (const p of quarterPayroll) {
        if (!payrollByEmployee[p.employeeId]) payrollByEmployee[p.employeeId] = 0;
        payrollByEmployee[p.employeeId] += p.grossPay;
      }

      // Group billable hours by employee
      const hoursByEmployee = {};
      for (const h of hoursData) {
        const empId = h.employee_id || empByName[h.user_display_name];
        if (!empId) continue;

        if (!hoursByEmployee[empId]) hoursByEmployee[empId] = 0;
        hoursByEmployee[empId] += h.duration_hours;
      }

      // Get all unique employee IDs
      const allEmployeeIds = new Set([
        ...Object.keys(payrollByEmployee).map(Number),
        ...Object.keys(hoursByEmployee).map(Number)
      ]);

      const results = [];
      for (const empId of allEmployeeIds) {
        const totalPay = payrollByEmployee[empId] || 0;
        const totalHours = hoursByEmployee[empId] || 0;

        const rate = totalHours > 0
            ? Math.round(
              (
                totalPay *
                (1 + this.WORKERS_COMP_RATE) *
                this.RECOVERABLE_PAYROLL_SHARE /
                totalHours
              ) * 100
            ) / 100
            : 0;

        // Upsert snapshot
        const { data, error } = await supabase
          .from('employee_rate_snapshots')
          .upsert({
            employee_id: empId,
            quarter_start: quarterStart,
            quarter_end: quarterEnd,
            total_gross_pay: Math.round(totalPay * 100) / 100,
            total_project_hours: Math.round(totalHours * 100) / 100,
            calculated_rate: rate,
            updated_at: new Date().toISOString()
          }, { onConflict: 'employee_id,quarter_start' })
          .select()
          .single();

        if (error) {
          console.error(`Error upserting snapshot for employee ${empId}:`, error);
        } else {
          results.push(data);
        }
      }

      return results;
    } catch (error) {
      console.error('Error generating quarterly snapshot:', error);
      throw error;
    }
  }

  /**
   * Generate snapshots for all quarters with payroll data
   */
  static async generateAllQuarterlySnapshots() {
    const quarters = [];
    const payroll = await this.getAllPayroll();

    if (payroll.length === 0) {
      return { message: 'No payroll data found', quarters: [] };
    }

    // Find date range
    const dates = payroll.map(p => new Date(p.payPeriodStart));
    const minDate = new Date(Math.min(...dates));
    const maxDate = new Date();

    // Generate quarter boundaries
    let current = new Date(minDate.getFullYear(), Math.floor(minDate.getMonth() / 3) * 3, 1);
    while (current <= maxDate) {
      const qStart = current.toISOString().split('T')[0];
      const qEnd = new Date(current.getFullYear(), current.getMonth() + 3, 0).toISOString().split('T')[0];
      quarters.push({ start: qStart, end: qEnd });
      current.setMonth(current.getMonth() + 3);
    }

    const results = [];
    for (const q of quarters) {
      const snapshots = await this.generateQuarterlySnapshot(q.start, q.end);
      results.push({ quarter: q, snapshots });
    }

    return { message: `Generated snapshots for ${quarters.length} quarters`, quarters: results };
  }

  /**
   * Backfill labor_cost on existing manictime_tags
   * @param {string} startDate - Start date (YYYY-MM-DD)
   * @param {string} endDate - End date (YYYY-MM-DD)
   */
  static async backfillLaborCosts(startDate, endDate) {
    try {
      // Get rate snapshots
      const { data: snapshots } = await supabase
        .from('employee_rate_snapshots')
        .select('*');

      // Build lookup: employee_id -> { quarterStart -> rate }
      const rateLookup = {};
      for (const s of snapshots) {
        if (!rateLookup[s.employee_id]) rateLookup[s.employee_id] = {};
        rateLookup[s.employee_id][s.quarter_start] = s;
      }

      // Get employee name -> ID mapping
      const { data: employees } = await supabase.from('employees').select('id, name');
      const empByName = {};
      employees.forEach(e => { empByName[e.name] = e.id; });

      // Get all time entries in range
      const { data: entries, error } = await supabase
        .from('manictime_tags')
        .select('id, employee_id, user_display_name, project_id, duration_hours, start_time')
        .gte('start_time', startDate)
        .lte('start_time', endDate + 'T23:59:59')
        .order('start_time', { ascending: true });

      if (error) throw error;

      let updated = 0;
      let skipped = 0;
      const batchSize = 100;
      const updates = [];

      for (const entry of entries) {
        const empId = entry.employee_id || empByName[entry.user_display_name];
        if (!empId) {
          skipped++;
          continue;
        }

        const isBillable = Boolean(entry.project_id);

        let rate = null;
        let snapshotId = null;

        if (isBillable) {
          // Find quarter for this entry
          const entryDate = new Date(entry.start_time);
          const qStart = new Date(entryDate.getFullYear(), Math.floor(entryDate.getMonth() / 3) * 3, 1)
            .toISOString().split('T')[0];

          const snapshot = rateLookup[empId]?.[qStart];
          if (snapshot) {
            rate = snapshot.calculated_rate;
            snapshotId = snapshot.id;
          }
        }

        updates.push({
          id: entry.id,
          employee_id: empId,
          is_billable: isBillable,
          labor_cost: isBillable && rate ? Math.round(entry.duration_hours * rate * 100) / 100 : null,
          hourly_rate_used: isBillable ? rate : null,
          rate_snapshot_id: snapshotId
        });

        // Batch update
        if (updates.length >= batchSize) {
          for (const u of updates) {
            const { error: updateError } = await supabase
              .from('manictime_tags')
              .update({
                employee_id: u.employee_id,
                is_billable: u.is_billable,
                labor_cost: u.labor_cost,
                hourly_rate_used: u.hourly_rate_used,
                rate_snapshot_id: u.rate_snapshot_id
              })
              .eq('id', u.id);

            if (!updateError) updated++;
          }
          updates.length = 0;
        }
      }

      // Final batch
      for (const u of updates) {
        const { error: updateError } = await supabase
          .from('manictime_tags')
          .update({
            employee_id: u.employee_id,
            is_billable: u.is_billable,
            labor_cost: u.labor_cost,
            hourly_rate_used: u.hourly_rate_used,
            rate_snapshot_id: u.rate_snapshot_id
          })
          .eq('id', u.id);

        if (!updateError) updated++;
      }

      return { updated, skipped, total: entries.length };
    } catch (error) {
      console.error('Error backfilling labor costs:', error);
      throw error;
    }
  }

  /**
   * Lock a quarter's rates (call after quarter ends)
   */
  static async lockQuarter(quarterStart) {
    try {
      const { data, error } = await supabase
        .from('employee_rate_snapshots')
        .update({ is_locked: true, updated_at: new Date().toISOString() })
        .eq('quarter_start', quarterStart)
        .eq('is_locked', false)
        .select();

      if (error) throw error;
      return { locked: data.length };
    } catch (error) {
      console.error('Error locking quarter:', error);
      throw error;
    }
  }
}
